import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { checkRole, userProfile } from 'utils/auth';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import DataGridTypes from 'types/DataGridTypes';
import { hasDealerContext } from 'utils/hasDealerContext';
import type { TestNurturingMainTab } from 'app/pages/TestNurturingPage/types';
import {
  buildTestNurturingBreadcrumbs,
  parseTestNurturingViewFromSearch,
  TEST_NURTURING_VIEW_PARAM,
} from 'app/pages/TestNurturingPage/utils/testNurturingRouteUtils';
import { useSegmentInventories } from 'app/pages/EmailsGridPage/hooks/useSegmentInventories';
import { emailsGridActions } from 'app/pages/EmailsGridPage/slice';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { saveGenerationState } from 'app/pages/EmailsGridPage/utils/generationPersistence';
import { useTestNurturingJourneysFilterTray } from 'app/pages/TestNurturingPage/hooks/useTestNurturingJourneysFilterTray';

/** Same privilege tier as legacy super-admin routes (see authRoutes roleToRouteMap). */
const TEST_NURTURING_PAGE_ROLES = [
  'super_user',
  'maiq_super_admin',
  'maiq_tech_user',
];

function normalizeRoles(roles: unknown): string[] {
  if (Array.isArray(roles)) return roles as string[];
  if (typeof roles === 'string' && roles.trim()) return [roles];
  return [];
}

export type TestTemplateGenerationStep = 1 | 2;

export function useTestNurturingPage() {
  const user = userProfile();
  const canAccess = checkRole(
    normalizeRoles(user?.roles),
    TEST_NURTURING_PAGE_ROLES,
  );

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const selectedDealer = useSelector(selectDealerSelected);
  const hasSelectedDealer = hasDealerContext(selectedDealer);
  const selectedDealerId =
    typeof selectedDealer === 'object' && selectedDealer !== null
      ? String(
          (selectedDealer as { dealer?: string; _id?: string }).dealer ||
            (selectedDealer as { dealer?: string; _id?: string })._id ||
            '',
        )
      : '';

  const activeMainTab = useMemo(
    () => parseTestNurturingViewFromSearch(location.search),
    [location.search],
  );

  const breadcrumbsList = useMemo(
    () => buildTestNurturingBreadcrumbs(activeMainTab),
    [activeMainTab],
  );

  const setActiveMainTab = useCallback(
    (tab: TestNurturingMainTab | ((prev: TestNurturingMainTab) => TestNurturingMainTab)) => {
      const resolved = typeof tab === 'function' ? tab(activeMainTab) : tab;
      const params = new URLSearchParams(location.search);
      params.set(TEST_NURTURING_VIEW_PARAM, resolved);
      const nextSearch = `?${params.toString()}`;
      if (location.search === nextSearch) {
        return;
      }
      history.push({
        pathname: location.pathname,
        search: nextSearch,
      });
    },
    [activeMainTab, history, location.pathname, location.search],
  );

  const [testTemplateGenerationModalOpen, setTestTemplateGenerationModalOpen] =
    useState(false);
  const [testTemplateGenerationStep, setTestTemplateGenerationStep] =
    useState<TestTemplateGenerationStep>(1);

  /**
   * Quick Filter state for the Nurturing Journeys grid. The popover anchor is
   * also tracked here so the parent component stays UI-only and we keep all
   * logic + state co-located in the page hook.
   */
  const [journeysFilterTrayOpen, setJourneysFilterTrayOpen] = useState(false);
  const [journeysFilterTrayAnchorEl, setJourneysFilterTrayAnchorEl] =
    useState<HTMLElement | null>(null);

  const journeysFilterTray = useTestNurturingJourneysFilterTray({
    dealerId: selectedDealerId,
    fetchEnabled: journeysFilterTrayOpen,
  });

  const openJourneysFilterTray = useCallback(
    (anchor: HTMLElement | null) => {
      setJourneysFilterTrayAnchorEl(anchor);
      setJourneysFilterTrayOpen(true);
    },
    [],
  );

  const closeJourneysFilterTray = useCallback(() => {
    setJourneysFilterTrayOpen(false);
    setJourneysFilterTrayAnchorEl(null);
  }, []);

  const journeysMetadataFiltersKey = useMemo(
    () => JSON.stringify(journeysFilterTray.metadataFilters),
    [journeysFilterTray.metadataFilters],
  );

  const leadsGridType = useMemo(() => {
    if (!selectedDealerId) return null;
    return DataGridTypes.TEST_NURTURING_JOURNEYS_LEADS(
      selectedDealerId,
      'test',
      journeysFilterTray.metadataFilters,
    );
  }, [selectedDealerId, journeysFilterTray.metadataFilters]);

  /**
   * Segment-inventories / preferences state for the Test Template Generation modal.
   * Loads when modal is opened. Reuses the exact same hook used by EmailsGridPage's PreferencesModal
   * so behavior (caching, missing VINs, save) is identical.
   */
  const segmentInventories = useSegmentInventories(
    selectedDealerId,
    testTemplateGenerationModalOpen,
  );

  /**
   * Live snapshot of preferred VINs by segment+kind. Drives the read-only confirmation view on
   * step 2 of the Test Template Generation modal. Recomputed only when the underlying selections
   * change so step 2 reflects edits made on step 1 (and any in-place edits before Generate).
   */
  const preferredVinsSnapshot = useMemo(
    () => segmentInventories.getPreferredVinsSnapshot(),
    [segmentInventories.getPreferredVinsSnapshot],
  );

  const openTestTemplateGenerationModal = useCallback(() => {
    // Force "Generated Templates" tab so the embedded EmailsGridPage is mounted before generation
    // starts. This pre-warms the socket subscription and progress-card host so there is no race
    // between dispatching generate and receiving the first socket events.
    setActiveMainTab('templates');
    setTestTemplateGenerationStep(1);
    setTestTemplateGenerationModalOpen(true);
  }, []);

  const closeTestTemplateGenerationModal = useCallback(() => {
    setTestTemplateGenerationModalOpen(false);
    setTestTemplateGenerationStep(1);
  }, []);

  /** Step 1 "Next": save preferences via PUT, then advance to step 2. Notification on success/failure. */
  const goToTemplateGenerationStep2 = useCallback(async () => {
    if (!selectedDealerId) return;
    try {
      // Only persist if user actually changed something or if backend ID is available;
      // when there are no changes we just advance to step 2 without an API hit.
      if (segmentInventories.canSavePreferences) {
        await segmentInventories.savePreferences();
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'success',
            message: 'Preferences saved successfully',
          }),
        );
      }
      setTestTemplateGenerationStep(2);

      // Kick off (non-blocking) prefetch for segment+kind combos that have selected VINs so the
      // step 2 confirmation view can render rich vehicle info (year/make/model/price/days in lot/CPO)
      // instead of bare VIN strings. Already-cached pairs are skipped automatically.
      const snapshot = segmentInventories.getPreferredVinsSnapshot();
      const keysToPrefetch = segmentInventories.segmentRows.flatMap(row => {
        const out: Array<{ segmentName: string; kind: 'new' | 'used' }> = [];
        if ((snapshot[`${row.segment_name}-new`] ?? []).length > 0) {
          out.push({ segmentName: row.segment_name, kind: 'new' });
        }
        if ((snapshot[`${row.segment_name}-used`] ?? []).length > 0) {
          out.push({ segmentName: row.segment_name, kind: 'used' });
        }
        return out;
      });
      if (keysToPrefetch.length > 0) {
        void segmentInventories.prefetchSegmentInventories(keysToPrefetch);
      }
    } catch (err: any) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: err?.message || 'Failed to save preferences',
        }),
      );
    }
  }, [selectedDealerId, segmentInventories, dispatch]);

  const goBackToTemplateGenerationStep1 = useCallback(() => {
    setTestTemplateGenerationStep(1);
  }, []);

  /**
   * Step 2 "Generate": Mirrors EmailsGridPage's `handleGenerateEmailTemplates` exactly so that
   * the EmailsGridPage (rendered on the Generated Templates tab) picks up the in-progress state
   * from Redux/localStorage and shows the same socket progress card, details modal, completion modal,
   * and grid refresh.
   *
   * Order matches EmailsGridPage:
   *   1. Reset progress in Redux
   *   2. Dispatch generate request (saga makes API call)
   *   3. Persist initial state to localStorage so the page can restore UI on mount
   *
   * We also force the active tab to "templates" so EmailsGridPage is mounted and its
   * `useEmailsGridSocket` subscription is active for the new dealer.
   */
  const triggerTemplateGenerationFromModal = useCallback(async () => {
    if (!selectedDealerId) return;
    try {
      // If user tweaked VINs on step 2 and there are unsaved changes, save them first so
      // backend generation uses the latest preferences.
      if (segmentInventories.canSavePreferences) {
        await segmentInventories.savePreferences();
      }

      // Ensure EmailsGridPage is mounted so socket cards / progress UI / datagrid refresh work.
      setActiveMainTab('templates');

      dispatch(
        emailsGridActions.setGenerateProgress({ current: 0, total: 1 }),
      );
      dispatch(
        emailsGridActions.requestGenerateEmailTemplates({
          dealerId: selectedDealerId,
        }),
      );

      saveGenerationState(selectedDealerId, {
        generateInProgress: true,
        progress: { current: 0, total: 1 },
        socketMessages: [],
        lastSocketMessage: null,
      });

      setTestTemplateGenerationModalOpen(false);
      setTestTemplateGenerationStep(1);
    } catch (err: any) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: err?.message || 'Failed to start template generation',
        }),
      );
    }
  }, [selectedDealerId, segmentInventories, dispatch]);

  return {
    canAccess,
    hasSelectedDealer,
    activeMainTab,
    setActiveMainTab,
    breadcrumbsList,
    selectedDealerId,
    segmentsLoading: false,
    segmentGroupId: '',
    leadsGridType,
    journeysFilterTray,
    journeysFilterTrayOpen,
    journeysFilterTrayAnchorEl,
    openJourneysFilterTray,
    closeJourneysFilterTray,
    journeysMetadataFiltersKey,
    testTemplateGenerationModalOpen,
    openTestTemplateGenerationModal,
    closeTestTemplateGenerationModal,
    testTemplateGenerationStep,
    goToTemplateGenerationStep2,
    goBackToTemplateGenerationStep1,
    triggerTemplateGenerationFromModal,
    segmentInventories,
    preferredVinsSnapshot,
  };
}
